'use client';

import { useState, useEffect } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatUSD, parseGridId } from '@/lib/utils';

interface PaybackCalculatorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxRateBps: number;
  floorPrice: number;
  avgWeeklyRewards: number;
  gridName?: string;
  gridId?: string;
  gridImageUrl?: string;
  activeTiles?: number;
  totalTiles?: number;
}

export function PaybackCalculatorModal({
  open,
  onOpenChange,
  taxRateBps,
  floorPrice,
  avgWeeklyRewards,
  gridName,
  gridId,
  gridImageUrl,
  activeTiles,
  totalTiles,
}: PaybackCalculatorModalProps) {
  const [tilePrice, setTilePrice] = useState<string>(() => floorPrice.toFixed(2));

  useEffect(() => {
    if (open) {
      setTilePrice(floorPrice.toFixed(2));
    }
  }, [open, floorPrice]);

  const tilePriceNum = parseFloat(tilePrice) || 0;
  const weeklyTax = (tilePriceNum * taxRateBps) / 10000;
  const dailyTax = weeklyTax / 7;
  const taxRatePct = (taxRateBps / 100).toFixed(1);
  const netWeekly = avgWeeklyRewards - weeklyTax;
  const breakevenWeeks =
    netWeekly > 0 ? Math.ceil(tilePriceNum / netWeekly) : null;

  const displayName = gridName || (gridId ? `Grid #${parseGridId(gridId).numericId}` : 'Grid');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            {gridImageUrl ? (
              <div className="w-7 h-7 rounded-md overflow-hidden border border-border/50 bg-muted shrink-0">
                <img src={gridImageUrl} alt="" className="w-full h-full object-cover" />
              </div>
            ) : gridId ? (
              <div className="w-7 h-7 rounded-md border border-border/50 bg-gradient-to-br from-primary/20 to-primary/5 shrink-0 flex items-center justify-center">
                <span className="text-[9px] font-bold text-muted-foreground">#{gridId}</span>
              </div>
            ) : null}
            <div className="min-w-0">
              <span className="block truncate">Payback Calculator</span>
              <span className="text-[11px] font-normal text-muted-foreground block truncate">
                {displayName}
                {activeTiles != null && totalTiles != null && (
                  <> &middot; {activeTiles}/{totalTiles} tiles &middot; {taxRatePct}% weekly tax</>
                )}
              </span>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Estimate breakeven and tax costs for tiles on this grid.
          </DialogDescription>
        </DialogHeader>

        {/* Volatility Warning */}
        <div className="flex gap-2.5 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200/90 leading-relaxed">
            <span className="font-semibold">This is not a passive yield strategy.</span> Rewards
            are extremely volatile and depend on trading activity. Tile holders must actively
            assess and manage their tile price to remain competitive.
          </p>
        </div>

        <div className="space-y-4">
          {/* Tile Price Input */}
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Tile Price (USD)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={tilePrice}
              onChange={(e) => setTilePrice(e.target.value)}
              className="bg-muted border border-border rounded-md px-3 py-1.5 text-sm font-mono w-full focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>

          {/* Calculated Values */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Weekly Tax</span>
              <span className="font-mono text-foreground">{formatUSD(weeklyTax)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Daily Tax</span>
              <span className="font-mono text-foreground">{formatUSD(dailyTax)}</span>
            </div>
            <div className="flex justify-between text-sm items-center">
              <span className="text-muted-foreground flex items-center gap-1">
                Est. Weekly Rewards
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 text-muted-foreground/50 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] text-xs leading-relaxed">
                    Based on the grid&apos;s average reward distribution over the past 7 days,
                    divided equally across active tiles. This figure is highly volatile,
                    changes constantly with trading activity, and is not a prediction or guarantee
                    of future earnings.
                  </TooltipContent>
                </Tooltip>
              </span>
              <span className="font-mono text-foreground">{formatUSD(avgWeeklyRewards)}</span>
            </div>
            <div className="flex justify-between text-sm pt-1 border-t border-border/30">
              <span className="text-muted-foreground">Net Weekly (Rewards − Tax)</span>
              <span className={`font-mono font-semibold ${netWeekly > 0 ? 'text-win-green' : netWeekly < 0 ? 'text-red-400' : 'text-foreground'}`}>
                {netWeekly >= 0 ? '+' : ''}{formatUSD(netWeekly)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Breakeven</span>
              <span className="font-mono text-foreground">
                {breakevenWeeks != null ? `~${breakevenWeeks} week${breakevenWeeks !== 1 ? 's' : ''}` : '—'}
              </span>
            </div>
          </div>

          {/* How it works */}
          <div className="space-y-2 pt-2 border-t border-border/50">
            <p className="text-[11px] font-medium text-foreground/70 uppercase tracking-wider">How it works</p>
            <p className="text-xs text-muted-foreground">
              Your tile costs a weekly tax of {taxRatePct}% of its self-assessed price.
              This tax is deducted from your deposit automatically.
            </p>
            <p className="text-xs text-muted-foreground">
              Rewards come from LP fees generated by the grid&apos;s assets and are
              distributed equally to all active tile holders.
            </p>
            <p className="text-xs text-muted-foreground">
              Lower your price to reduce tax, but anyone can buy your tile at that price.
              Higher prices protect your position but increase cost.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="pt-2 border-t border-border/50">
            <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
              This calculator provides estimates only and does not constitute financial advice.
              Actual rewards depend on trading volumes, LP fee generation, and the number of
              active tile holders — all of which can change at any time. Past performance is
              not indicative of future results. You may lose part or all of your deposit.
              Participate at your own risk.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
